import React, { useState, useEffect, useRef } from 'react';
import { socket } from '../App';
import { API_BASE_URL } from '../config';
import { parsePhoneNumberFromString, type CountryCode as PhoneCountryCode } from 'libphonenumber-js/min';
import {
    Loader2,
    ChevronDown,
    Search,
    CheckCircle2,
    AlertCircle,
    Smartphone
} from 'lucide-react';

interface PhoneNumberLoginProps {
    onLoginSuccess: (phoneNumber: string, userData: any) => void;
    onSwitchToQR?: () => void;
    initialCountryCode?: string | null;
}

interface CountryCode {
    code: string;
    name: string;
    flag: string;
    dial: string;
}

function findCountryByCode(countryCode?: string | null): CountryCode | undefined {
    if (!countryCode) {
        return undefined;
    }

    return COUNTRY_CODES.find(country => country.code === countryCode.toUpperCase());
}

function parsePhoneNumberSafely(input: string, defaultCountry?: PhoneCountryCode) {
    try {
        return defaultCountry
            ? parsePhoneNumberFromString(input, defaultCountry)
            : parsePhoneNumberFromString(input);
    } catch {
        return undefined;
    }
}

function tryExtractCountryFromInternationalInput(input: string): { country: CountryCode; nationalNumber: string } | null {
    const trimmedInput = input.trim();
    if (!trimmedInput.startsWith('+') && !trimmedInput.startsWith('00')) {
        return null;
    }

    const normalizedInput = trimmedInput.startsWith('00')
        ? `+${trimmedInput.slice(2)}`
        : trimmedInput;
    const parsed = parsePhoneNumberSafely(normalizedInput);

    if (!parsed?.country) {
        return null;
    }

    const country = findCountryByCode(parsed.country);
    if (!country) {
        return null;
    }

    return {
        country,
        nationalNumber: parsed.nationalNumber,
    };
}

function formatPhoneNumberForPairing(
    input: string,
    selectedCountry: CountryCode
): { e164Digits: string; detectedCountry?: CountryCode } | null {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
        return null;
    }

    const normalizedInternationalInput = trimmedInput.startsWith('00')
        ? `+${trimmedInput.slice(2)}`
        : trimmedInput;

    const parsed = normalizedInternationalInput.startsWith('+')
        ? parsePhoneNumberSafely(normalizedInternationalInput)
        : parsePhoneNumberSafely(trimmedInput, selectedCountry.code as PhoneCountryCode);

    if (!parsed || (!parsed.isValid() && !parsed.isPossible())) {
        return null;
    }

    return {
        e164Digits: parsed.number.replace(/^\+/, ''),
        detectedCountry: findCountryByCode(parsed.country),
    };
}

const COUNTRY_CODES: CountryCode[] = [
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', dial: '+93' },
    { code: 'AL', name: 'Albania', flag: '🇦🇱', dial: '+355' },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿', dial: '+213' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷', dial: '+54' },
    { code: 'AM', name: 'Armenia', flag: '🇦🇲', dial: '+374' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', dial: '+61' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹', dial: '+43' },
    { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', dial: '+994' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dial: '+973' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', dial: '+880' },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾', dial: '+375' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪', dial: '+32' },
    { code: 'BJ', name: 'Benin', flag: '🇧🇯', dial: '+229' },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴', dial: '+591' },
    { code: 'BA', name: 'Bosnia', flag: '🇧🇦', dial: '+387' },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼', dial: '+267' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', dial: '+55' },
    { code: 'BN', name: 'Brunei', flag: '🇧🇳', dial: '+673' },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', dial: '+359' },
    { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', dial: '+226' },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭', dial: '+855' },
    { code: 'CM', name: 'Cameroon', flag: '🇨🇲', dial: '+237' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', dial: '+1' },
    { code: 'TD', name: 'Chad', flag: '🇹🇩', dial: '+235' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱', dial: '+56' },
    { code: 'CN', name: 'China', flag: '🇨🇳', dial: '+86' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴', dial: '+57' },
    { code: 'CD', name: 'Congo (DRC)', flag: '🇨🇩', dial: '+243' },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', dial: '+506' },
    { code: 'CI', name: "Côte d'Ivoire", flag: '🇨🇮', dial: '+225' },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷', dial: '+385' },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺', dial: '+53' },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾', dial: '+357' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', dial: '+420' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', dial: '+45' },
    { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', dial: '+253' },
    { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', dial: '+1' },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨', dial: '+593' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬', dial: '+20' },
    { code: 'SV', name: 'El Salvador', flag: '🇸🇻', dial: '+503' },
    { code: 'ER', name: 'Eritrea', flag: '🇪🇷', dial: '+291' },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪', dial: '+372' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', dial: '+251' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮', dial: '+358' },
    { code: 'FR', name: 'France', flag: '🇫🇷', dial: '+33' },
    { code: 'GA', name: 'Gabon', flag: '🇬🇦', dial: '+241' },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲', dial: '+220' },
    { code: 'GE', name: 'Georgia', flag: '🇬🇪', dial: '+995' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', dial: '+49' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭', dial: '+233' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷', dial: '+30' },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹', dial: '+502' },
    { code: 'GN', name: 'Guinea', flag: '🇬🇳', dial: '+224' },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹', dial: '+509' },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳', dial: '+504' },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', dial: '+852' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺', dial: '+36' },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸', dial: '+354' },
    { code: 'IN', name: 'India', flag: '🇮🇳', dial: '+91' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', dial: '+62' },
    { code: 'IR', name: 'Iran', flag: '🇮🇷', dial: '+98' },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶', dial: '+964' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪', dial: '+353' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱', dial: '+972' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', dial: '+39' },
    { code: 'JM', name: 'Jamaica', flag: '🇯🇲', dial: '+1' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', dial: '+81' },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴', dial: '+962' },
    { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿', dial: '+7' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', dial: '+254' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dial: '+965' },
    { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', dial: '+996' },
    { code: 'LA', name: 'Laos', flag: '🇱🇦', dial: '+856' },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻', dial: '+371' },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dial: '+961' },
    { code: 'LY', name: 'Libya', flag: '🇱🇾', dial: '+218' },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹', dial: '+370' },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', dial: '+352' },
    { code: 'MG', name: 'Madagascar', flag: '🇲🇬', dial: '+261' },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼', dial: '+265' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dial: '+60' },
    { code: 'ML', name: 'Mali', flag: '🇲🇱', dial: '+223' },
    { code: 'MR', name: 'Mauritania', flag: '🇲🇷', dial: '+222' },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺', dial: '+230' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', dial: '+52' },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩', dial: '+373' },
    { code: 'MN', name: 'Mongolia', flag: '🇲🇳', dial: '+976' },
    { code: 'ME', name: 'Montenegro', flag: '🇲🇪', dial: '+382' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦', dial: '+212' },
    { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', dial: '+258' },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲', dial: '+95' },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦', dial: '+264' },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵', dial: '+977' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dial: '+31' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dial: '+64' },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', dial: '+505' },
    { code: 'NE', name: 'Niger', flag: '🇳🇪', dial: '+227' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dial: '+234' },
    { code: 'KP', name: 'North Korea', flag: '🇰🇵', dial: '+850' },
    { code: 'MK', name: 'North Macedonia', flag: '🇲🇰', dial: '+389' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', dial: '+47' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲', dial: '+968' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dial: '+92' },
    { code: 'PS', name: 'Palestine', flag: '🇵🇸', dial: '+970' },
    { code: 'PA', name: 'Panama', flag: '🇵🇦', dial: '+507' },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾', dial: '+595' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪', dial: '+51' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭', dial: '+63' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', dial: '+48' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', dial: '+351' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦', dial: '+974' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴', dial: '+40' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', dial: '+7' },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼', dial: '+250' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dial: '+966' },
    { code: 'SN', name: 'Senegal', flag: '🇸🇳', dial: '+221' },
    { code: 'RS', name: 'Serbia', flag: '🇷🇸', dial: '+381' },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', dial: '+232' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', dial: '+65' },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰', dial: '+421' },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮', dial: '+386' },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴', dial: '+252' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dial: '+27' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', dial: '+82' },
    { code: 'SS', name: 'South Sudan', flag: '🇸🇸', dial: '+211' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', dial: '+34' },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', dial: '+94' },
    { code: 'SD', name: 'Sudan', flag: '🇸🇩', dial: '+249' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', dial: '+46' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dial: '+41' },
    { code: 'SY', name: 'Syria', flag: '🇸🇾', dial: '+963' },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼', dial: '+886' },
    { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', dial: '+992' },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', dial: '+255' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', dial: '+66' },
    { code: 'TG', name: 'Togo', flag: '🇹🇬', dial: '+228' },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳', dial: '+216' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷', dial: '+90' },
    { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', dial: '+993' },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬', dial: '+256' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦', dial: '+380' },
    { code: 'AE', name: 'UAE', flag: '🇦🇪', dial: '+971' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dial: '+44' },
    { code: 'US', name: 'United States', flag: '🇺🇸', dial: '+1' },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾', dial: '+598' },
    { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', dial: '+998' },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪', dial: '+58' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', dial: '+84' },
    { code: 'YE', name: 'Yemen', flag: '🇾🇪', dial: '+967' },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲', dial: '+260' },
    { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', dial: '+263' },
];

// Detect user's country from browser locale/language
function detectUserCountry(): CountryCode {
    try {
        // Try Intl API first (most reliable)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        const timezoneToCountry: Record<string, string> = {
            'Asia/Baghdad': 'IQ', 'Asia/Basra': 'IQ',
            'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US', 'America/Los_Angeles': 'US',
            'Europe/London': 'GB', 'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
            'Europe/Berlin': 'DE', 'Europe/Paris': 'FR', 'Europe/Rome': 'IT', 'Europe/Madrid': 'ES',
            'Asia/Tokyo': 'JP', 'Asia/Seoul': 'KR', 'Asia/Shanghai': 'CN', 'Asia/Hong_Kong': 'HK',
            'Australia/Sydney': 'AU', 'America/Toronto': 'CA', 'America/Sao_Paulo': 'BR',
            'America/Mexico_City': 'MX', 'Europe/Moscow': 'RU', 'Asia/Dubai': 'AE',
            'Asia/Riyadh': 'SA', 'Asia/Karachi': 'PK', 'Asia/Dhaka': 'BD',
            'Asia/Jakarta': 'ID', 'Asia/Kuala_Lumpur': 'MY', 'Asia/Manila': 'PH',
            'Asia/Bangkok': 'TH', 'Asia/Ho_Chi_Minh': 'VN', 'Asia/Singapore': 'SG',
            'Africa/Cairo': 'EG', 'Africa/Lagos': 'NG', 'Africa/Nairobi': 'KE',
            'Africa/Johannesburg': 'ZA', 'Europe/Istanbul': 'TR', 'Asia/Jerusalem': 'IL',
            'Asia/Colombo': 'LK', 'Asia/Kathmandu': 'NP', 'Africa/Accra': 'GH',
            'Europe/Amsterdam': 'NL', 'Europe/Brussels': 'BE', 'Europe/Stockholm': 'SE',
            'Europe/Oslo': 'NO', 'Europe/Copenhagen': 'DK', 'Europe/Helsinki': 'FI',
            'Europe/Warsaw': 'PL', 'Europe/Lisbon': 'PT', 'Europe/Athens': 'GR',
            'Asia/Tehran': 'IR', 'Asia/Amman': 'JO', 'Asia/Beirut': 'LB',
            'Asia/Damascus': 'SY', 'Asia/Kuwait': 'KW', 'Asia/Bahrain': 'BH',
            'Asia/Qatar': 'QA', 'Asia/Muscat': 'OM', 'Asia/Aden': 'YE',
            'Africa/Tripoli': 'LY', 'Africa/Tunis': 'TN', 'Africa/Algiers': 'DZ',
            'Africa/Casablanca': 'MA', 'Africa/Khartoum': 'SD', 'Europe/Kiev': 'UA',
            'Pacific/Auckland': 'NZ', 'America/Buenos_Aires': 'AR', 'America/Santiago': 'CL',
            'America/Bogota': 'CO', 'America/Lima': 'PE',
        };
        const tzCountry = timezoneToCountry[timezone];
        if (tzCountry) {
            const found = COUNTRY_CODES.find(c => c.code === tzCountry);
            if (found) return found;
        }

        // Fallback: navigator.language (e.g. "ar-IQ", "en-US")
        const lang = navigator.language || (navigator as any).userLanguage || '';
        const parts = lang.split('-');
        if (parts.length >= 2) {
            const regionCode = parts[parts.length - 1].toUpperCase();
            const found = COUNTRY_CODES.find(c => c.code === regionCode);
            if (found) return found;
        }
    } catch (e) {
        // ignore detection errors
    }
    // Default fallback
    return COUNTRY_CODES.find(c => c.code === 'US') || COUNTRY_CODES[0];
}

type LoginStep = 'phone' | 'code' | 'connecting' | 'success' | 'error';

export function PhoneNumberLogin({ onLoginSuccess, onSwitchToQR, initialCountryCode }: PhoneNumberLoginProps) {
    const [step, setStep] = useState<LoginStep>('phone');
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() => findCountryByCode(initialCountryCode) || detectUserCountry());
    const [phoneNumber, setPhoneNumber] = useState('');
    const [pairingCode, setPairingCode] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // Store data while waiting for sync
    const [connectedPhoneNumber, setConnectedPhoneNumber] = useState<string | null>(null);
    const [contacts, setContacts] = useState<any[]>([]);
    const [chats, setChats] = useState<any[]>([]);
    const [messages, setMessages] = useState<{ [jid: string]: any[] }>({});

    // Filter countries based on search
    const filteredCountries = COUNTRY_CODES.filter(country => 
        country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.dial.includes(countrySearch) ||
        country.code.toLowerCase().includes(countrySearch.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowCountryDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const preferredCountry = findCountryByCode(initialCountryCode);
        if (preferredCountry && !phoneNumber.trim() && step === 'phone') {
            setSelectedCountry(preferredCountry);
        }
    }, [initialCountryCode, phoneNumber, step]);

    // Socket event listeners
    useEffect(() => {
        function onPairingCode(code: string) {
            console.log('[PhoneLogin] Received pairing code:', code);
            setPairingCode(code);
            setStep('code');
            setIsSubmitting(false);
        }

        function onConnected(data: { phoneNumber: string }) {
            console.log('[PhoneLogin] Connected:', data.phoneNumber);
            setConnectedPhoneNumber(data.phoneNumber);
            // Don't call onLoginSuccess yet - wait for sync complete
            setStep('success');
        }

        function onContacts(contactsList: any[]) {
            console.log('[PhoneLogin] Received contacts:', contactsList.length);
            setContacts(contactsList);
        }

        function onChats(chatsList: any[]) {
            console.log('[PhoneLogin] Received chats:', chatsList.length);
            setChats(chatsList);
        }

        function onMessages(messagesList: { [jid: string]: any[] }) {
            const totalMessages = Object.values(messagesList).reduce((sum, msgs) => sum + msgs.length, 0);
            console.log('[PhoneLogin] Received messages:', totalMessages);
            setMessages(messagesList);
        }

        function onSyncComplete() {
            console.log('[PhoneLogin] Sync complete');
            // Sync complete will trigger the effect below
        }

        function onError(data: { message: string }) {
            console.error('[PhoneLogin] Error:', data.message);
            setError(data.message);
            setStep('error');
            setIsSubmitting(false);
        }

        socket.on('whatsapp-pairing-code', onPairingCode);
        socket.on('whatsapp-connected', onConnected);
        socket.on('whatsapp-contacts', onContacts);
        socket.on('whatsapp-chats', onChats);
        socket.on('whatsapp-messages', onMessages);
        socket.on('whatsapp-sync-complete', onSyncComplete);
        socket.on('whatsapp-error', onError);

        return () => {
            socket.off('whatsapp-pairing-code', onPairingCode);
            socket.off('whatsapp-connected', onConnected);
            socket.off('whatsapp-contacts', onContacts);
            socket.off('whatsapp-chats', onChats);
            socket.off('whatsapp-messages', onMessages);
            socket.off('whatsapp-sync-complete', onSyncComplete);
            socket.off('whatsapp-error', onError);
        };
    }, []);

    // Call onLoginSuccess when we have all the data
    useEffect(() => {
        if (connectedPhoneNumber && step === 'success') {
            // Give a small delay to ensure we have all the data
            const timer = setTimeout(() => {
                console.log('[PhoneLogin] Calling onLoginSuccess with:', contacts.length, 'contacts,', chats.length, 'chats,', Object.keys(messages).length, 'message threads');
                onLoginSuccess(connectedPhoneNumber, {
                    contacts,
                    chats,
                    messages
                });
            }, 3000);  // Wait 3 seconds to ensure sync data arrives
            return () => clearTimeout(timer);
        }
    }, [connectedPhoneNumber, step, contacts, chats, messages, onLoginSuccess]);

    const handleSubmitPhone = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!phoneNumber.trim()) {
            setError('Please enter your phone number');
            return;
        }

        const parsedPhone = formatPhoneNumberForPairing(phoneNumber, selectedCountry);
        if (!parsedPhone) {
            setError('Enter a valid phone number');
            return;
        }

        if (parsedPhone.detectedCountry) {
            setSelectedCountry(parsedPhone.detectedCountry);
        }

        const fullNumber = parsedPhone.e164Digits;

        setIsSubmitting(true);
        setError(null);
        console.log('[PhoneLogin] Requesting pairing code for:', fullNumber);

        try {
            const response = await fetch(`${API_BASE_URL}/whatsapp/login-phone`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    phoneNumber: fullNumber,
                    socketId: socket.id  // Pass socket ID for per-user session
                }),
            });

            const data = await response.json();
            
            if (!data.success) {
                setError(data.message || 'Failed to start phone login');
                setStep('error');
                setIsSubmitting(false);
            }
            // If success, wait for socket event with pairing code
        } catch (err) {
            setError('Network error. Please try again.');
            setStep('error');
            setIsSubmitting(false);
        }
    };

    const handleRetry = () => {
        setStep('phone');
        setError(null);
        setPairingCode(null);
        setPhoneNumber('');
    };

    const handlePhoneNumberChange = (value: string) => {
        const normalizedInput = value.replace(/[^\d+\s()-]/g, '');
        const internationalMatch = tryExtractCountryFromInternationalInput(normalizedInput);

        if (internationalMatch) {
            setSelectedCountry(internationalMatch.country);
            setPhoneNumber(internationalMatch.nationalNumber);
            return;
        }

        setPhoneNumber(normalizedInput.replace(/[^0-9 ]/g, ''));
    };

    return (
        <div className="max-w-md mx-auto">
            {step === 'phone' && (
                <form onSubmit={handleSubmitPhone} className="space-y-6">
                    {/* Header - matches WhatsApp Web */}
                    <div className="text-center mb-2">
                        <h2 className="text-2xl font-normal text-gray-800 mb-2">Enter phone number</h2>
                        <p className="text-gray-500 text-sm">
                            Select a country and enter your phone number.
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="space-y-4 px-4">
                        {/* Country Selector - pill shaped like WhatsApp */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCountryDropdown(!showCountryDropdown);
                                    setCountrySearch('');
                                }}
                                className="w-full border border-gray-300 rounded-full px-5 py-3 flex items-center gap-3 hover:border-gray-400 transition-colors bg-white"
                            >
                                <span className="text-xl">{selectedCountry.flag}</span>
                                <span className="text-base text-gray-800 flex-1 text-left">{selectedCountry.name}</span>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                            </button>

                            {showCountryDropdown && (
                                <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-80 overflow-hidden">
                                    {/* Search bar */}
                                    <div className="sticky top-0 p-3 border-b border-gray-100 bg-white">
                                        <div className="relative">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                value={countrySearch}
                                                onChange={(e) => setCountrySearch(e.target.value)}
                                                placeholder="Search country or code..."
                                                className="w-full bg-gray-50 border border-gray-200 text-gray-800 pl-10 pr-4 py-2.5 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    {/* Country list */}
                                    <div className="overflow-y-auto max-h-60">
                                        {filteredCountries.map(country => (
                                            <button
                                                key={country.code}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedCountry(country);
                                                    setShowCountryDropdown(false);
                                                    setCountrySearch('');
                                                }}
                                                className={`w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                                                    selectedCountry.code === country.code 
                                                        ? 'bg-green-50' 
                                                        : ''
                                                }`}
                                            >
                                                <span className="text-xl">{country.flag}</span>
                                                <span className="text-gray-800 flex-1 text-left text-sm">{country.name}</span>
                                                <span className="text-gray-400 text-sm font-mono">{country.dial}</span>
                                                {selectedCountry.code === country.code && (
                                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                )}
                                            </button>
                                        ))}
                                        {filteredCountries.length === 0 && (
                                            <div className="px-4 py-6 text-center text-gray-400 text-sm">
                                                No countries found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Phone Number Input - pill shaped with dial code inside */}
                        <div className="flex items-center border border-gray-300 rounded-full px-5 py-3 gap-2 focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500 transition-all bg-white">
                            <span className="text-gray-700 font-normal text-base select-none whitespace-nowrap">
                                {selectedCountry.dial}
                            </span>
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                                placeholder="Phone number"
                                className="flex-1 bg-transparent text-gray-800 text-base placeholder-gray-400 focus:outline-none"
                                autoComplete="tel-national"
                            />
                        </div>
                    </div>

                    {/* Submit button - green pill */}
                    <div className="flex justify-center pt-2">
                        <button
                            type="submit"
                            disabled={isSubmitting || !phoneNumber.trim()}
                            className="bg-green-500 hover:bg-green-600 active:bg-green-700 text-white px-10 py-2.5 rounded-full font-medium flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-sm"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Requesting...
                                </>
                            ) : (
                                'Next'
                            )}
                        </button>
                    </div>

                    {/* Switch to QR - matches WhatsApp style */}
                    {onSwitchToQR && (
                        <div className="text-center pt-1">
                            <button
                                type="button"
                                onClick={onSwitchToQR}
                                className="text-green-600 hover:text-green-700 text-sm transition-colors font-normal inline-flex items-center gap-1"
                            >
                                Log in with QR code <span className="text-xs">›</span>
                            </button>
                        </div>
                    )}
                </form>
            )}

            {step === 'code' && pairingCode && (
                <div className="text-center space-y-6">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Your Pairing Code</h2>
                        <p className="text-gray-500 text-sm">Enter this code in WhatsApp to link this device</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-2xl p-6 shadow-inner">
                        <div className="text-4xl font-mono font-bold text-green-700 tracking-widest">
                            {pairingCode.match(/.{1,4}/g)?.join('-') || pairingCode}
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-left">
                        <div className="flex items-start gap-3">
                            <Smartphone className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-gray-800 mb-2">How to enter the code:</p>
                                <ol className="list-decimal list-inside space-y-1.5 text-gray-600 text-sm">
                                    <li>Open <strong>WhatsApp</strong> on your phone</li>
                                    <li>Go to <strong>Settings → Linked Devices</strong></li>
                                    <li>Tap <strong>"Link a Device"</strong></li>
                                    <li>When camera opens, tap <strong className="text-green-600">"Link with phone number instead"</strong> at the bottom</li>
                                    <li>Enter your phone number when asked</li>
                                    <li>Enter the <strong>8-digit code</strong> shown above</li>
                                </ol>
                            </div>
                        </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                        <p className="text-amber-700 text-xs">
                            ⏱️ Code expires in ~2 minutes. Make sure WhatsApp is updated to the latest version.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 text-gray-500 text-sm justify-center">
                        <Loader2 className="w-4 h-4 animate-spin text-green-500" />
                        Waiting for connection...
                    </div>

                    <div className="flex flex-col gap-2">
                        <button
                            onClick={handleRetry}
                            className="text-gray-500 hover:text-green-600 text-sm transition-colors font-medium"
                        >
                            ← Use a different number
                        </button>
                        {onSwitchToQR && (
                            <button
                                onClick={onSwitchToQR}
                                className="text-blue-500 hover:text-blue-600 text-sm transition-colors font-medium"
                            >
                                📷 Use QR Code instead
                            </button>
                        )}
                    </div>
                </div>
            )}

            {step === 'success' && (
                <div className="text-center space-y-6">
                    <div className="w-16 h-16 mx-auto">
                        <Loader2 className="w-16 h-16 animate-spin text-green-500" />
                    </div>
                    
                    <div>
                        <p className="text-gray-600 text-lg">Processing your request...</p>
                    </div>
                </div>
            )}

            {step === 'error' && (
                <div className="text-center space-y-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <AlertCircle className="w-10 h-10 text-white" />
                    </div>
                    
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Connection Failed</h2>
                        <p className="text-gray-500">{error || 'Something went wrong'}</p>
                    </div>

                    <button
                        onClick={handleRetry}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-medium transition-colors border border-gray-200"
                    >
                        Try Again
                    </button>
                </div>
            )}
        </div>
    );
}
